/**
 * NatureSpeciesProfile — /nature/species/:slug
 * Canonical profile page for one species or natural place. Body is
 * AI-generated on first visit (polls until ready).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, MapPin, ExternalLink, PlaySquare, Leaf, ShieldAlert, Camera,
} from "lucide-react";
import { http } from "@/lib/api";
import AutoLinkText from "@/components/AutoLinkText";
import MediaEmbed from "@/components/MediaEmbed";
import EntityMessageBoard from "@/components/EntityMessageBoard";

export default function NatureSpeciesProfile() {
  const { slug } = useParams();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingImg, setRegeneratingImg] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get(`/nature/atlas/entities/${encodeURIComponent(slug)}`);
      setEntity(data);
      return data;
    } catch (e) {
      setError(e?.response?.data?.detail || "Not found");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    let poll;
    let startTs = Date.now();
    (async () => {
      const first = await load();
      // Poll while body is still queued OR generating.
      if (first && (first.body_status === "generating" || first.body_status === "queued")) {
        poll = setInterval(async () => {
          if (!alive) return;
          const next = await load();
          if (!next) { clearInterval(poll); return; }
          if (next.body || next.body_status === "ready" || next.body_status === "error") {
            clearInterval(poll);
          }
          // Give up after ~5 minutes to avoid infinite polling.
          if (Date.now() - startTs > 5 * 60 * 1000) clearInterval(poll);
        }, 5000);
      }
    })();
    return () => { alive = false; if (poll) clearInterval(poll); };
    // eslint-disable-next-line
  }, [slug]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await http.post(`/nature/atlas/entities/${encodeURIComponent(slug)}/regenerate`, {});
      setEntity(data);
    } catch (e) { /* noop */ }
    finally { setRegenerating(false); }
  };

  const regenerateImage = async () => {
    setRegeneratingImg(true);
    try {
      const { data } = await http.post(`/nature/atlas/entities/${encodeURIComponent(slug)}/regenerate-image`, {});
      setEntity(data);
    } catch (e) { /* noop */ }
    finally { setRegeneratingImg(false); }
  };

  if (loading && !entity) return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="nature-species-loading">
      <div className="wofy-card !p-6 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    </div>
  );
  if (error) return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="wofy-card !p-6" style={{ background: "#FEF3C7" }}>{error}</div>
    </div>
  );
  if (!entity) return null;

  const b = entity.body || {};
  const bodyReady = !!entity.body;
  const isSpecies = entity.kind === "species";
  const isPlace   = entity.kind === "place";
  const ytUrl = b.youtube_search_query
    ? `https://www.youtube.com/results?search_query=${encodeURIComponent(b.youtube_search_query)}`
    : null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="nature-species-profile">
      <Link to={`/nature/atlas/${entity.category}`}
            className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> {entity.category}
      </Link>

      <header className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1 flex items-center gap-2">
          <span>{entity.category}{entity.kind ? ` · ${entity.kind}` : ""}</span>
          {entity.user_requested && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest"
              style={{ background: "#fef3c7", color: "#b45309", border: "1px solid #fde68a" }}
              title={entity.user_requested_name
                ? `Requested by ${entity.user_requested_name} — thanks for making the atlas grow!`
                : "This profile was created because a visitor searched for it — thank you for making the atlas grow!"}
              data-testid="nature-species-community-ribbon"
            >
              ✨ Requested{entity.user_requested_name ? ` by ${entity.user_requested_name}` : " by the community"}
            </span>
          )}
        </div>
        <h1 className="font-display text-4xl leading-tight">{entity.name}</h1>
        {entity.scientific_name && (
          <div className="text-lg italic font-mono opacity-80 mt-1">{entity.scientific_name}</div>
        )}
        {entity.tagline && <p className="opacity-90 mt-2">{entity.tagline}</p>}
      </header>

      {/* Enrichment progress banner — visible while a freshly-requested
          profile is being generated. Auto-hides once body_status flips. */}
      {(entity.body_status === "queued" || entity.body_status === "generating") && !bodyReady && (
        <div
          className="rounded-2xl border overflow-hidden mb-6"
          style={{
            borderColor: "var(--wofy-primary)",
            background: "linear-gradient(135deg,#eef2ff 0%,#f0fdfa 100%)",
          }}
          data-testid="nature-species-enrichment-progress"
        >
          <div className="p-4 sm:p-5 flex items-center gap-3">
            <div className="shrink-0 w-11 h-11 rounded-2xl bg-white flex items-center justify-center text-2xl shadow-sm">
              🧬
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg leading-snug flex items-center gap-2">
                Building your profile
                <Loader2 className="w-4 h-4 animate-spin opacity-70" />
              </div>
              <p className="text-sm opacity-80 mt-0.5">
                We're pulling Wikipedia sources and generating the full
                field guide — habitat, diet, behaviour and more. Usually
                takes about a minute.
              </p>
            </div>
          </div>
          {/* Indeterminate progress bar */}
          <div className="h-1 relative overflow-hidden bg-black/5">
            <style>{`
              @keyframes np-slide { 0%{transform:translateX(-100%);} 100%{transform:translateX(400%);} }
            `}</style>
            <div
              className="absolute inset-y-0 w-1/4"
              style={{
                background: "linear-gradient(90deg, transparent, var(--wofy-primary), transparent)",
                animation: "np-slide 1.6s ease-in-out infinite",
              }}
            />
          </div>
        </div>
      )}

      {/* Hero illustration (AI-generated via Gemini Nano Banana when
          the entity has no user-supplied photo). */}
      {entity.photo_url && (
        <div className="rounded-2xl overflow-hidden border mb-6"
             style={{ borderColor: "var(--wofy-border)" }}
             data-testid="nature-species-hero-image">
          <img src={entity.photo_url} alt={entity.name}
               className="w-full max-h-[420px] object-cover bg-black/5" />
        </div>
      )}
      {!entity.photo_url && entity.image_status === "generating" && (
        <div className="wofy-card !p-4 mb-6 inline-flex items-center gap-2"
             data-testid="nature-species-image-generating">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--wofy-primary)" }} />
          <div className="text-sm">Illustrating this species with AI — refresh in a moment…</div>
        </div>
      )}

      {!bodyReady && (
        <div className="wofy-card !p-6 mb-6 inline-flex items-center gap-2" data-testid="nature-body-generating">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--wofy-primary)" }} />
          <div>
            <div className="font-medium">Preparing an AI-authored profile…</div>
            <div className="text-xs opacity-70">This happens once, then it stays. Refresh in ~30 seconds if it doesn't appear.</div>
          </div>
        </div>
      )}

      {bodyReady && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-6">
          <div className="space-y-5 min-w-0">
            {b.short_summary && (
              <p className="text-lg opacity-90"><AutoLinkText text={b.short_summary} perEntityMax={2} /></p>
            )}
            {b.overview && (
              <section>
                <h2 className="font-display text-2xl mb-2">Overview</h2>
                <p className="whitespace-pre-line opacity-90"><AutoLinkText text={b.overview} perEntityMax={3} /></p>
              </section>
            )}

            {isSpecies && b.identification && (
              <Section title="How to identify" text={b.identification} icon={Leaf} />
            )}
            {b.habitat        && <Section title="Habitat"       text={b.habitat} />}
            {b.distribution   && <Section title="Distribution"  text={b.distribution} icon={MapPin} />}
            {isSpecies && b.diet          && <Section title="Diet"          text={b.diet} />}
            {isSpecies && b.behavior      && <Section title="Behaviour"     text={b.behavior} />}
            {isSpecies && b.reproduction  && <Section title="Reproduction & life cycle" text={b.reproduction} />}
            {isSpecies && b.seasons       && <Section title="When to look"   text={b.seasons} />}
            {isSpecies && b.cultural_significance && <Section title="Cultural significance" text={b.cultural_significance} />}
            {isPlace   && b.getting_there && <Section title="Getting there" text={b.getting_there} />}
            {isPlace   && b.best_time     && <Section title="Best time to visit" text={b.best_time} />}
            {isPlace   && b.trails        && <Section title="Trails & points of interest" text={b.trails} />}
            {isPlace   && b.wildlife      && <Section title="Wildlife you might see" text={b.wildlife} />}
            {isPlace   && b.flora         && <Section title="Flora" text={b.flora} />}
            {isPlace   && b.safety_tips   && <Section title="Safety & preparation" text={b.safety_tips} />}
            {b.key_points?.length > 0 && (
              <section>
                <h2 className="font-display text-2xl mb-2">Key points</h2>
                <ul className="list-disc pl-5 space-y-1 opacity-90">
                  {b.key_points.map((k, i) => <li key={i}><AutoLinkText text={k} perEntityMax={1} /></li>)}
                </ul>
              </section>
            )}

            {(b.similar_species || []).length > 0 && (
              <section>
                <h2 className="font-display text-2xl mb-2">Similar species</h2>
                <ul className="space-y-1 text-sm opacity-90">
                  {b.similar_species.map((s, i) => (
                    <li key={i}>
                      {typeof s === "string" ? s : (
                        <><strong>{s.common_name || s.name}</strong>
                        {s.scientific_name ? <em className="opacity-70"> — {s.scientific_name}</em> : null}
                        {s.how_to_tell_apart ? <span className="opacity-80"> · {s.how_to_tell_apart}</span> : null}</>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {ytUrl && (
              <section data-testid="nature-yt-search">
                <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-1.5">
                  <PlaySquare className="w-5 h-5" /> Watch on YouTube
                </h2>
                <a href={ytUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 hover:underline"
                   style={{ color: "var(--wofy-primary)" }}>
                  Search YouTube for "{b.youtube_search_query}" <ExternalLink className="w-3 h-3" />
                </a>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 self-start space-y-4">
            {b.quick_facts && Object.values(b.quick_facts).some(Boolean) && (
              <div className="wofy-card !p-4" data-testid="nature-quick-facts">
                <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>QUICK FACTS</div>
                <dl className="space-y-1.5 text-sm">
                  {Object.entries(b.quick_facts).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}>
                      <dt className="opacity-60 text-xs uppercase font-mono tracking-wider">{k.replace(/_/g, " ")}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {b.conservation_status && (
              <div className="wofy-card !p-4" data-testid="nature-conservation">
                <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>CONSERVATION</div>
                <div className="text-sm">{b.conservation_status}</div>
              </div>
            )}
            {(b.further_reading || []).length > 0 && (
              <div className="wofy-card !p-4">
                <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>FURTHER READING</div>
                <ul className="text-sm space-y-1.5">
                  {b.further_reading.map((u, i) => (
                    <li key={i}>
                      <a href={u} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 hover:underline"
                         style={{ color: "var(--wofy-primary)" }}>
                        <ExternalLink className="w-3 h-3" />
                        {new URL(u).host}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {b.safety_note && (
              <div className="wofy-card !p-4" style={{ background: "#FEF3C7" }} data-testid="nature-safety-note">
                <div className="overline mb-1 inline-flex items-center gap-1" style={{ color: "#92400E" }}>
                  <ShieldAlert className="w-3 h-3" /> IMPORTANT
                </div>
                <p className="text-sm">{b.safety_note}</p>
              </div>
            )}
            <div className="wofy-card !p-4">
              <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>SEE THIS?</div>
              <p className="text-xs opacity-80 mb-3">
                Photograph it and let our AI identify what you found — it may match this species.
              </p>
              <Link to="/nature/identify" className="wofy-btn-primary w-full text-sm" data-testid="nature-identify-from-species">
                <Camera className="w-4 h-4" /> Identify a photo
              </Link>
            </div>
            <button type="button" onClick={regenerate} disabled={regenerating}
                    className="wofy-btn-ghost w-full text-xs opacity-70 hover:opacity-100"
                    data-testid="nature-regenerate">
              {regenerating ? "Refreshing…" : "Ask AI to improve this profile"}
            </button>
            <button type="button" onClick={regenerateImage} disabled={regeneratingImg}
                    className="wofy-btn-ghost w-full text-xs opacity-70 hover:opacity-100"
                    data-testid="nature-regenerate-image">
              {regeneratingImg
                ? "Generating illustration…"
                : (entity.photo_url ? "Regenerate illustration" : "Generate an AI illustration")}
            </button>
          </aside>
        </div>
      )}

      {/* Community discussion — always available, regardless of body status */}
      {entity && (
        <EntityMessageBoard
          entityType="nature"
          entityId={slug}
          title={`Discussion · ${entity.name || slug}`}
        />
      )}
    </div>
  );
}

function Section({ title, text, icon: Icon }) {
  return (
    <section>
      <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-5 h-5" />} {title}
      </h2>
      <p className="whitespace-pre-line opacity-90"><AutoLinkText text={text} perEntityMax={2} /></p>
    </section>
  );
}
